import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('jiffys_cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('jiffys_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item) => {
    setCartItems(prev => {
      // Check if item already exists
      const existingIndex = prev.findIndex(i => 
        i.id === item.id && i.type === item.type
      );
      
      if (existingIndex >= 0) {
        // Update quantity
        const updated = [...prev];
        updated[existingIndex].quantity = (updated[existingIndex].quantity || 1) + 1;
        return updated;
      }
      
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (id, type) => {
    setCartItems(prev => prev.filter(item => !(item.id === id && item.type === type)));
  };

  const updateQuantity = (id, type, quantity) => {
    if (quantity <= 0) {
      removeFromCart(id, type);
      return;
    }
    setCartItems(prev => prev.map(item => 
      item.id === id && item.type === type 
        ? { ...item, quantity } 
        : item
    ));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.price * (item.quantity || 1);
      // Add 7% tax for services
      if (item.type === 'service') {
        return total + price * 1.07;
      }
      // Add 1% platform fee for marketplace
      if (item.type === 'product') {
        return total + price * 1.01;
      }
      return total + price;
    }, 0);
  };

  const getServiceTotal = () => {
    return cartItems
      .filter(item => item.type === 'service')
      .reduce((total, item) => total + (item.price * (item.quantity || 1) * 1.07), 0);
  };

  const getProductTotal = () => {
    return cartItems
      .filter(item => item.type === 'product')
      .reduce((total, item) => total + (item.price * (item.quantity || 1) * 1.01), 0);
  };

  const cartCount = cartItems.reduce((count, item) => count + (item.quantity || 1), 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getServiceTotal,
      getProductTotal,
      cartCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
